import { STATUT_RETIRE } from "./depo.js";
import { indexDemiJournee } from "./planning.js";
/**
 * Les lignes d'une tâche qui ont encore une place dans le temps : toutes, sauf les lignes
 * **supprimées** de la contre-étude.
 *
 * *Chris, 28/10/2026, sur ALDI MIMIZAN : « sur le 2.4 la barre va jusqu'à J43 alors qu'il n'y a
 * aucune tâche en dessous qui va jusqu'à cette date », puis « je ne vois pas la pelle 5 t dans
 * le planning ».* Supprimer une ligne laisse ses barres en base — la réintégrer les retrouve —,
 * et le planning la cache depuis le 10/09. Mais le résumé, lui, lisait encore ses barres : la
 * barre grise s'étirait sur une ligne invisible, et le 2.1 se facturait en deux échéances
 * (un morceau de 582,61 € que rien ne permettait de retirer à l'écran). Une ligne supprimée ne
 * coûte rien, n'occupe personne : elle ne résume rien non plus.
 */
export function lignesPlanifiees(tache) {
    return tache.ressources.filter((r) => r.statut !== STATUT_RETIRE);
}
/**
 * Les barres d'une tâche qui comptent dans un résumé.
 *
 * Ce n'est **pas** la liste des barres à dessiner : la barre d'une ressource exclue reste
 * affichée dans le détail, sur toute sa longueur, et c'est la moitié de la demande. Ce qui
 * change est seulement ce qu'un titre replié résume.
 *
 * Les barres portées par la tâche elle-même passent toujours : elles n'ont pas de ressource,
 * donc pas de drapeau au référentiel, et rien ne les exclut. Celles d'une ligne supprimée,
 * jamais (voir `lignesPlanifiees`).
 */
export function barresDuResume(tache) {
    return [
        ...tache.planning.phases,
        ...lignesPlanifiees(tache).filter((r) => r.impacteBarreGrise).flatMap((r) => r.planning.phases),
    ];
}
/**
 * Les pastilles d'une tâche, à dessiner quand aucune barre ne la résume.
 *
 * *Demande de Chris, 27/08/2026 : « une demande concernant les pastilles qui ne devait pas
 * impacter les barres grises des chapitres […] il faut aussi que cela n'impacte pas les barres
 * grises des sous-chapitres », puis, sur DFCI MAILLAS : « j'ai mis deux pastilles sur amené et
 * repli, et quand je mets une pastille sur jour 4 la barre grise s'étire, cela ne va pas ».*
 *
 * ## UNE PASTILLE N'ENTRE JAMAIS DANS UN RÉSUMÉ
 *
 * Le premier correctif l'écartait seulement des résumés qui ont des barres, et laissait les
 * pastilles porter le résumé d'une tâche qui n'en a aucune — pour qu'un poste de fournitures
 * pures ne disparaisse pas du planning replié. **Le contre-exemple est arrivé dans l'heure**, et
 * il dit exactement pourquoi la moitié conservée était fausse : « amené et repli matériel », ce
 * sont deux rotations, une au J1 et une au J4. Les résumer par une barre continue affirme que
 * le poste occupe le chantier quatre jours durant, alors qu'il ne s'y passe rien entre les deux.
 *
 * *C'est la distinction du 12/08 prise au sérieux : une barre est une durée, une pastille est un
 * instant.* Une enveloppe d'instants n'est pas une durée — c'est un intervalle inventé.
 *
 * ## Ce qui remplace la barre disparue
 *
 * La crainte qui avait retenu la règle était réelle : les pastilles ne se dessinent que sur les
 * lignes du DÉTAIL, donc une tâche sans barre n'aurait plus rien montré une fois repliée. Elle
 * ne se traite pas en gonflant le résumé mais en **dessinant ses pastilles sur la ligne du
 * titre**, à leur jour, sans rien entre elles. On voit alors ce qui est vrai : deux camions,
 * deux jours.
 *
 * Rendue ici plutôt que dans l'écran pour que la condition — *aucune barre ne résume cette
 * tâche* — soit exactement celle qui fait disparaître la barre grise, et pas une autre écrite à
 * côté qui finirait par en différer.
 */
export function pastillesDuTitre(tache) {
    if (barresDuResume(tache).length > 0)
        return { livraisons: [], transports: [] };
    return {
        livraisons: lignesPlanifiees(tache).flatMap((ressource) => ressource.planning.livraisons.map((livraison) => ({ ressource, livraison }))),
        transports: lignesPlanifiees(tache).flatMap((ressource) => ressource.planning.transports.map((transport) => ({ ressource, transport }))),
    };
}
/**
 * Les PÉRIODES de travail d'un ensemble de barres : leur étendue coupée à chaque trou.
 *
 * *Chris, 25/09/2026, sur la tâche 1.5 d'APO260098 hachée en trois reprises : « il ne faut pas
 * que la barre grise soit continue, elle doit faire J5 puis J9 puis J16 ».*
 *
 * Un trou, c'est au moins une demi-journée qu'**aucune** barre n'occupe. Les jours relatifs
 * étant des jours ouvrés, un week-end ne coupe rien. Deux barres qui se touchent ou se
 * chevauchent font une seule période. Rendues dans l'ordre, bornes INCLUSES, comme
 * `etendueEnDemiJournees` — dont l'enveloppe va du `debut` de la première au `fin` de la
 * dernière.
 */
export function periodesEnDemiJournees(barres) {
    const intervalles = barres
        .map((b) => {
        const debut = indexDemiJournee(b);
        return { debut, fin: debut + b.demiJournees - 1 };
    })
        .sort((a, b) => a.debut - b.debut);
    const periodes = [];
    for (const { debut, fin } of intervalles) {
        const derniere = periodes.at(-1);
        if (derniere !== undefined && debut <= derniere.fin + 1) {
            if (fin > derniere.fin)
                derniere.fin = fin;
        }
        else {
            periodes.push({ debut, fin });
        }
    }
    return periodes;
}
/**
 * Les MORCEAUX d'une barre grise : ses périodes, recoupées à chaque départ de barre.
 *
 * *Chris, 28/09/2026 : « il faudrait aussi pouvoir prendre la dernière barre de tâche hachée et
 * la déplacer, là on doit bouger les ressources une par une pour séparer les barres ».*
 *
 * Le hachage pose la reprise qu'il ajoute **collée** à la dernière : elle ne fait donc aucun
 * trou, et `periodesEnDemiJournees` la fond dans la période d'avant. Sur la 1.5 d'APO260098
 * (8 demi-journées en cinq), le cinquième clic a bien écrit 2-2-2-1-1, mais la barre grise
 * montrait toujours quatre morceaux de 2 — on aurait dit qu'il ne s'était rien passé.
 *
 * Un morceau commence là où une barre commence ; il finit juste avant le départ suivant, ou à
 * la fin de sa période. Ce qu'on saisit est ce que le serveur déplace : les barres qui
 * COMMENCENT dans le morceau (`PerimetreDecalage.periode`). Une barre qui commence avant et
 * déborde dedans reste là où elle est.
 *
 * La **facturation ne lit pas ceci** : elle reste par période. Un morceau collé se facture avec
 * la période qu'il touche, et prend son propre montant dès qu'on l'en détache.
 */
export function morceauxEnDemiJournees(barres) {
    const departs = [...new Set(barres.map((b) => indexDemiJournee(b)))].sort((a, b) => a - b);
    return periodesEnDemiJournees(barres).flatMap((periode) => {
        const coupes = departs.filter((d) => d > periode.debut && d <= periode.fin);
        const bornes = [periode.debut, ...coupes];
        return bornes.map((debut, rang) => ({
            debut,
            fin: rang + 1 < bornes.length ? bornes[rang + 1] - 1 : periode.fin,
        }));
    });
}
//# sourceMappingURL=barre-grise.js.map